// NewNG API Service
// Handles all backend communication for the NewNG system

import { NewNGJob, NewNGHeadline, NewNGProgram } from '../types';

class NewNGApiService {
  private baseUrl = '/news/api';

  /**
   * Get all available news jobs
   */
  async getJobs(): Promise<NewNGJob[]> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs/`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.jobs || [];
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw new Error('Failed to fetch news jobs');
    }
  }

  /**
   * Get headlines for a specific job
   */
  async getJobHeadlines(jobId: string): Promise<NewNGHeadline[]> {
    try {
      // Use direct path like overlay.js does
      const response = await fetch(`/news/out/${jobId}/newsevent_stage/newsevent_stage_aggregation.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      // Transform the data to match our NewNGHeadline interface
      const events = data.news_events?.events || [];
      return events.map((event: any, index: number) => ({
        id: `headline-${index}`,
        title: event.headline || 'Breaking News',
        summary: event.summary || '',
        category: this.mapCategory(event.category),
        priority: this.mapPriority(event.priority),
        is_live: event.is_live || false,
        timestamp: event.timestamp || new Date().toISOString()
      }));
    } catch (error) {
      // Silent error - don't spam debug console
      throw new Error('Failed to fetch headlines');
    }
  }

  /**
   * Get program schedule
   */
  async getPrograms(): Promise<NewNGProgram[]> {
    // For now, return mock data - this would be replaced with actual API call
    return [
      {
        id: 'program-1',
        title: 'NewNG Morning News',
        description: 'The latest breaking news and top stories',
        start_time: '06:00',
        end_time: '09:00',
        is_live: true,
        host: 'Sarah Johnson',
        category: 'news'
      },
      {
        id: 'program-2',
        title: 'Business Today',
        description: 'Market updates and business analysis',
        start_time: '09:00',
        end_time: '10:00',
        is_live: false,
        host: 'Michael Chen',
        category: 'business'
      },
      {
        id: 'program-3',
        title: 'Technology Update',
        description: 'Latest tech news and innovations',
        start_time: '10:00',
        end_time: '11:00',
        is_live: false,
        host: 'Alex Rivera',
        category: 'technology'
      }
    ];
  }

  /**
   * Get video URL for a specific job
   */
  async getVideoUrl(jobId: string, quality: 'auto' | 'high' | 'medium' | 'low' = 'auto', orientation?: 'portrait' | 'landscape'): Promise<string> {
    try {
      const params = new URLSearchParams({ quality });
      if (orientation) {
        params.append('orientation', orientation);
      }

      const response = await fetch(`${this.baseUrl}/jobs/${jobId}/video?${params}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.video_url || '';
    } catch (error) {
      console.error('Error fetching video URL:', error);
      throw new Error('Failed to fetch video URL');
    }
  }

  /**
   * Get video URL for specific orientation
   */
  async getVideoUrlForOrientation(jobId: string, orientation: 'portrait' | 'landscape'): Promise<string> {
    try {
      // Try API first
      const response = await fetch(`/api/jobs/${jobId}/video?orientation=${orientation}`);
      if (response.ok) {
        const data = await response.json();
        return data.url;
      }
    } catch (error) {
      // API failed, use direct path
    }

    // Fallback to direct path
    const path = this.getVideoPath(jobId, orientation);
    return path;
  }

  /**
   * Get job status
   */
  async getJobStatus(jobId: string): Promise<string> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs/${jobId}/status`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.status || 'unknown';
    } catch (error) {
      console.error('Error fetching job status:', error);
      throw new Error('Failed to fetch job status');
    }
  }

  /**
   * Get detailed job metadata including artifacts
   */
  async getJobMetadata(jobId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/jobs/${jobId}/artifacts`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching job metadata:', error);
      throw new Error('Failed to fetch job metadata');
    }
  }

  /**
   * Get job metadata from the job root folder
   */
  async getJobInfo(jobId: string): Promise<any> {
    try {
      const response = await fetch(`/news/out/${jobId}/job_metadata.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching job info:', error);
      // Return null if job metadata doesn't exist (not all jobs have this)
      return null;
    }
  }

  /**
   * Get script from scriptgen stage
   */
  async getJobScript(jobId: string): Promise<any> {
    try {
      const response = await fetch(`/news/out/${jobId}/scriptgen_stage/scriptgen_stage_script.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching job script:', error);
      return null;
    }
  }

  /**
   * Get composition output data with timeout protection
   */
  async getCompositionOutput(jobId: string, timeoutMs: number = 5000): Promise<any> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(`/news/out/${jobId}/composition_stage/composition_stage_output.json`, {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn(`Composition output fetch timeout for job ${jobId}`);
      } else {
        console.warn(`Error fetching composition output for job ${jobId}:`, error);
      }
      return null;
    }
  }

  /**
   * Get news aggregation data from newsevent stage
   */
  async getJobAggregation(jobId: string): Promise<any> {
    try {
      const response = await fetch(`/news/out/${jobId}/newsevent_stage/newsevent_stage_aggregation.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching job aggregation:', error);
      return null;
    }
  }

  /**
   * Get enhanced job information combining metadata, script, and aggregation
   */
  async getEnhancedJobInfo(jobId: string): Promise<any> {
    try {
      const [metadata, script, aggregation] = await Promise.all([
        this.getJobInfo(jobId),
        this.getJobScript(jobId),
        this.getJobAggregation(jobId)
      ]);

      return {
        metadata,
        script,
        aggregation,
        jobId
      };
    } catch (error) {
      console.error('Error fetching enhanced job info:', error);
      return null;
    }
  }

    /**
   * Construct video path for a job with orientation support
   */
  getVideoPath(jobId: string, orientation: 'portrait' | 'landscape' = 'landscape'): string {
    // Original overlay.js mapping (keeping as was before):
    // portrait = video_1280x720p.mp4, landscape = video_720x1280p.mp4
    const path = orientation === 'portrait'
      ? `/news/out/${jobId}/composition_stage/video_1280x720p.mp4`  // 1280x720 = Portrait mode
      : `/news/out/${jobId}/composition_stage/video_720x1280p.mp4`; // 720x1280 = Landscape mode

    return path;
  }

  /**
   * Get newsevent stage metadata (resilient, with timeout)
   */
  async getNewseventMetadata(jobId: string, timeoutMs: number = 5000): Promise<any> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      const response = await fetch(`/news/out/${jobId}/newsevent_stage/newsevent_stage_metadata.json`, {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.warn(`Newsevent metadata fetch timeout for job ${jobId}`);
      } else {
        console.warn(`Error fetching newsevent metadata for job ${jobId}:`, error);
      }
      return null;
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return response.ok;
    } catch (error) {
      console.error('Health check failed:', error);
      return false;
    }
  }

  // Helper methods for data transformation
  private mapCategory(category: string): NewNGHeadline['category'] {
    const categoryMap: Record<string, NewNGHeadline['category']> = {
      'breaking': 'breaking',
      'politics': 'politics',
      'business': 'business',
      'technology': 'technology',
      'sports': 'sports',
      'entertainment': 'entertainment'
    };
    return categoryMap[category] || 'breaking';
  }

  private mapPriority(priority: string): NewNGHeadline['priority'] {
    const priorityMap: Record<string, NewNGHeadline['priority']> = {
      'high': 'high',
      'medium': 'medium',
      'low': 'low'
    };
    return priorityMap[priority] || 'medium';
  }
}

// Export singleton instance
export const newNGApi = new NewNGApiService();